import { useCallback, useEffect, useState } from "react";
import { useAsync } from "./useAsync";
import { useDefinition } from "./useDefinition";
import { useEditor } from "./useEditor";
import { useLogicalSelectedPage } from "./useSelection";

const defaultTemplates: readonly eXpress.core.Template[] = [];

/**
 * Load all templates for the current product.
 * We currently don't use the Template Categories
 * I think I would create a separate hook for that and combine them where needed
 */
function useAllTemplates() {
	const definition = useDefinition();
	const { client } = useEditor();

	const loadTemplatesAsync = useCallback(async () => {
		if (!definition) return defaultTemplates;
		return client.loadTemplatesAsync(
			definition.product.designTypeId,
			definition.designId,
			definition.templateStyle,
			definition.product.classification,
		);
	}, [definition, client]);

	// TODO: use react-query
	return useAsync(loadTemplatesAsync, {
		defaultValue: defaultTemplates,
	});
}

/**
 * Get the templates supported for the currently selected page
 * I think we don't need a page as argument at this moment
 */
export function usePageTemplates() {
	const allTemplatesAsyncValue = useAllTemplates();
	const { value: allTemplates, state: allTemplatesState } = allTemplatesAsyncValue;
	const page = useLogicalSelectedPage();
	const [pageTemplates, setPageTemplates] = useState(defaultTemplates);
	const spreadKind = page?.spread.kind;

	// Update if spreadKind changes
	useEffect(() => {
		// No page/spreadKind = No templates
		if (!spreadKind) {
			setPageTemplates(defaultTemplates);
			return;
		}

		setPageTemplates(
			allTemplates.filter(
				(template) =>
					template.supportedSpreadKinds.has(spreadKind) && page?.canChangeDesign(),
			),
		);
	}, [allTemplates, page, spreadKind]);

	// TODO:
	// Currently the same structure is returned as getAllTemplates
	// This would later become the return structure of react-query?
	return allTemplatesState === eXpress.AsyncState.Loaded
		? eXpress.createLoadedAsyncValue(pageTemplates)
		: allTemplatesAsyncValue; // Return the loading/error of useAllTemplates
}

/**
 * Returns if templates are supported for the current product
 * Currently returns also false when check is still loading */
export function useAreTemplatesSupported() {
	const definition = useDefinition();
	const { value: templates } = useAllTemplates();

	if (!definition) return false;
	if (!definition.canChangeDesign()) return false;

	return templates.length > 0; // There is no MA flag to known upfront if templates are available
}
